// Environment Configuration
export interface AppConfig {
  environment: 'development' | 'staging' | 'production';
  apiBaseUrl: string;
  wordpressUrl: string;
  appName: string;
  appVersion: string;
  firebase: {
    apiKey: string;
    authDomain: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
    measurementId?: string;
  };
  emailService: {
    serviceId: string;
    templateId: string;
    publicKey: string;
  };
  analytics: {
    googleAnalyticsId?: string;
    sentryDsn?: string;
  };
  security: {
    enableCsrfProtection: boolean;
    sessionTimeout: number;
  };
  features: {
    enablePwa: boolean;
    enableAnalytics: boolean;
    enableErrorTracking: boolean;
  };
}

// Default configuration
const defaultConfig: AppConfig = {
  environment: (process.env.REACT_APP_ENVIRONMENT as any) || 'development',
  apiBaseUrl: process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000',
  wordpressUrl: process.env.REACT_APP_WORDPRESS_URL || 'https://pop3iproperties.com',
  appName: process.env.REACT_APP_APP_NAME || 'POP3i Properties',
  appVersion: process.env.REACT_APP_APP_VERSION || '1.0.0',
  firebase: {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
    appId: process.env.REACT_APP_FIREBASE_APP_ID || '',
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  },
  emailService: {
    serviceId: process.env.REACT_APP_EMAILJS_SERVICE_ID || '',
    templateId: process.env.REACT_APP_EMAILJS_TEMPLATE_ID || '',
    publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY || '',
  },
  analytics: {
    googleAnalyticsId: process.env.REACT_APP_GOOGLE_ANALYTICS_ID,
    sentryDsn: process.env.REACT_APP_SENTRY_DSN,
  },
  security: {
    enableCsrfProtection: process.env.REACT_APP_ENABLE_CSRF_PROTECTION === 'true',
    sessionTimeout: parseInt(process.env.REACT_APP_SESSION_TIMEOUT || '3600000'),
  },
  features: {
    enablePwa: process.env.REACT_APP_ENABLE_PWA === 'true',
    enableAnalytics: process.env.REACT_APP_ENABLE_ANALYTICS === 'true',
    enableErrorTracking: process.env.REACT_APP_ENABLE_ERROR_TRACKING === 'true',
  },
};

// Validation function
export const validateConfig = (config: AppConfig): void => {
  const requiredFields = [
    'firebase.apiKey',
    'firebase.authDomain',
    'firebase.projectId',
    'firebase.storageBucket',
    'firebase.messagingSenderId',
    'firebase.appId',
  ];

  const missingFields = requiredFields.filter(field => {
    const keys = field.split('.');
    let value = config as any;
    for (const key of keys) {
      value = value?.[key];
    }
    return !value;
  });

  if (missingFields.length > 0) {
    throw new Error(`Missing required configuration fields: ${missingFields.join(', ')}`);
  }
};

// Export validated config
export const config = defaultConfig;

// Validate configuration on import
if (config.environment === 'production') {
  validateConfig(config);
}
